import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { FormValues } from '@/lib/types';
import type { SopAnalysisResult, TestLateralization } from '@/components/sop-intake/sop-analyzer/types';
import { cn } from '@/lib/utils';
import { SOP_ACCENT_THEME, SOP_BIAS_THEME, SOP_SECTION_LABELS, SOP_SECTION_ORDER } from '@/components/sop-intake/sop-analyzer/sop-theme';
import { getTestExplanationLines } from '@/components/sop-intake/sop-analyzer/lateralization-explainer';
import {
  filterLateralizationTests,
  isCervicalBatteryVisible,
} from '@/components/sop-intake/sop-analyzer/visible-battery';
import bodyTemplateUrl from '@/config/body template.png';
import { IoArrowRedoOutline, IoArrowUndoOutline, IoArrowForward, IoArrowBack } from 'react-icons/io5';
import { PiArrowArcRightBold, PiArrowArcLeftBold } from 'react-icons/pi';
import { TbX } from 'react-icons/tb';
import { VscArrowBoth } from 'react-icons/vsc';
import type { IconType } from 'react-icons';

export type MapArrowScale = 'S' | 'M' | 'L';
export type MapColorTheme = 'default' | 'high-contrast' | 'colorblind';

export interface LateralizationMapSettings {
  showLimbArrows: boolean;
  arrowScale: MapArrowScale;
  colorTheme: MapColorTheme;
  showSectionLabels: boolean;
  showLegend: boolean;
  backgroundOpacity: number;
}

export const DEFAULT_LATERALIZATION_MAP_SETTINGS: LateralizationMapSettings = {
  showLimbArrows: false,
  arrowScale: 'M',
  colorTheme: 'default',
  showSectionLabels: true,
  showLegend: true,
  backgroundOpacity: 0.85,
};

export interface BodyDiagramProps {
  analysis: SopAnalysisResult;
  formValues: FormValues;
  settings?: Partial<LateralizationMapSettings>;
  selectedRegion?: (typeof SOP_SECTION_ORDER)[number];
  onRegionClick?: (region: (typeof SOP_SECTION_ORDER)[number]) => void;
  /** When set, only tests in this battery appear on the map. */
  visibleTestKeys?: ReadonlySet<string>;
}

type Bias = 'Left' | 'Right' | 'Even' | null;

const sectionValues = (tests: TestLateralization[], section: string): string => {
  const relevant = tests.filter((t) => t.section === section);
  if (relevant.length === 0) return 'No tests';
  return relevant
    .map((t) => `${t.testLabel}: L=${t.leftVal ?? '—'} / R=${t.rightVal ?? '—'} (goal ${t.nominal})`)
    .join('\n');
};

interface IconPlacement {
  x: number;
  y: number;
  size: number;
}

const SECTION_HIT_ZONES: Record<(typeof SOP_SECTION_ORDER)[number], { x: number; y: number; w: number; h: number }> = {
  cervical: { x: 102, y: 18, w: 96, h: 82 },
  thorax: { x: 68, y: 102, w: 164, h: 112 },
  pelvis: { x: 88, y: 224, w: 126, h: 92 },
  femoral: { x: 82, y: 316, w: 136, h: 128 },
};

const PLACEMENTS = {
  torso: { x: 150, y: 158, size: 96 },
  pelvis: { x: 150, y: 264, size: 88 },
  car: { x: 150, y: 63, size: 22 },
  clf: { x: 150, y: 31, size: 16 },
  elbowLeft: { x: 80, y: 194, size: 26 },
  elbowRight: { x: 220, y: 194, size: 26 },
  kneeLeft: { x: 112, y: 370, size: 26 },
  kneeRight: { x: 188, y: 370, size: 26 },
} as const;

const resolveEqualIcon = (leftVal: number | null, rightVal: number | null): IconType | null =>
  leftVal !== null && rightVal !== null && leftVal === rightVal ? VscArrowBoth : null;

const resolveFacingIcon = (bias: Bias, leftVal: number | null = null, rightVal: number | null = null): IconType | null => {
  if (bias === null) return null;
  if (bias === 'Even') {
    const equalIcon = resolveEqualIcon(leftVal, rightVal);
    if (equalIcon) return equalIcon;
    if (leftVal !== null && rightVal !== null) return leftVal > rightVal ? IoArrowForward : IoArrowBack;
    return null;
  }
  return bias === 'Left' ? IoArrowForward : IoArrowBack;
};

const resolveClfIcon = (bias: Bias, leftVal: number | null = null, rightVal: number | null = null): IconType | null => {
  if (bias === null) return null;
  if (bias === 'Even') {
    const equalIcon = resolveEqualIcon(leftVal, rightVal);
    if (equalIcon) return equalIcon;
    if (leftVal !== null && rightVal !== null) return leftVal > rightVal ? PiArrowArcRightBold : PiArrowArcLeftBold;
    return null;
  }
  return bias === 'Left' ? PiArrowArcRightBold : PiArrowArcLeftBold;
};

const resolveLimbIcon = (irVal: number | null, erVal: number | null): IconType | null => {
  if (irVal === null || erVal === null) return null;
  const delta = irVal - erVal;
  if (delta === 0) return TbX;
  return delta > 0 ? IoArrowRedoOutline : IoArrowUndoOutline;
};

const formatValue = (value: number | null): string => (value === null ? 'N/A' : `${value}°`);

const buildDirectionalTooltip = (title: string, test: TestLateralization | undefined): string[] => {
  if (!test) return [title, 'No data available for this indicator.'];
  return [title, ...getTestExplanationLines(test)];
};

const buildLimbTooltip = (
  title: string,
  sideLabel: 'Left' | 'Right',
  irTest: TestLateralization | undefined,
  erTest: TestLateralization | undefined
): string[] => {
  const irValue = sideLabel === 'Left' ? irTest?.leftVal ?? null : irTest?.rightVal ?? null;
  const erValue = sideLabel === 'Left' ? erTest?.leftVal ?? null : erTest?.rightVal ?? null;
  
  if (irValue === null || erValue === null) {
    return [
      title,
      `Decision: Missing IR or ER value.`,
      `Values: IR=${formatValue(irValue)}, ER=${formatValue(erValue)}`
    ];
  }

  const delta = irValue - erValue;
  if (delta === 0) {
    return [
      title,
      `Decision: Equal Rotation.`,
      `Values: IR=${irValue}°, ER=${erValue}°`,
      `Contributing: ${irTest?.testLabel} & ${erTest?.testLabel}`
    ];
  }

  const biasLabel = delta > 0 ? 'IR-biased' : 'ER-biased';
  return [
    title,
    `Decision: ${biasLabel}`,
    `Values: IR=${irValue}°, ER=${erValue}° (Delta: ${Math.abs(delta)}°)`,
    `Contributing: ${irTest?.testLabel} & ${erTest?.testLabel}`
  ];
};

const PlacedIcon = ({
  icon: Icon,
  placement,
  color,
  isHyper,
  isInvalid,
  tooltipLines,
}: {
  icon: IconType | null;
  placement: IconPlacement;
  color: string;
  isHyper: boolean;
  isInvalid: boolean;
  tooltipLines: string[];
}) => {
  if (!Icon) return null;
  return (
    <foreignObject
      x={placement.x - placement.size / 2}
      y={placement.y - placement.size / 2}
      width={placement.size}
      height={placement.size}
      className="overflow-visible"
    >
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className={cn(
                'flex h-full w-full cursor-help items-center justify-center rounded-full',
                isHyper && 'ring-2 ring-yellow-400',
                isInvalid && 'opacity-40'
              )}
              aria-label={tooltipLines[0] ?? 'Indicator'}
            >
              <Icon size={placement.size} color={color} />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs text-xs">
            {tooltipLines.map((line, index) => (
              <p key={`${line}-${index}`} className={index === 0 ? 'font-semibold text-foreground' : 'text-muted-foreground'}>
                {line}
              </p>
            ))}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </foreignObject>
  );
};

export default function BodyDiagram({
  analysis,
  formValues: _formValues,
  settings,
  selectedRegion,
  onRegionClick,
  visibleTestKeys,
}: BodyDiagramProps) {
  const resolvedSettings: LateralizationMapSettings = {
    ...DEFAULT_LATERALIZATION_MAP_SETTINGS,
    ...settings,
  };
  const arrowScaleMultiplier = resolvedSettings.arrowScale === 'S' ? 0.85 : resolvedSettings.arrowScale === 'L' ? 1.2 : 1;
  const colorPalettes: Record<MapColorTheme, { left: string; right: string; even: string; na: string }> = {
    default: {
      left: SOP_BIAS_THEME.Left.hex,
      right: SOP_BIAS_THEME.Right.hex,
      even: SOP_BIAS_THEME.Even.hex,
      na: SOP_BIAS_THEME['N/A'].hex,
    },
    'high-contrast': {
      left: '#1d4ed8',
      right: '#b91c1c',
      even: '#15803d',
      na: '#334155',
    },
    colorblind: {
      left: '#2563eb',
      right: '#f97316',
      even: '#64748b',
      na: '#475569',
    },
  };
  const palette = colorPalettes[resolvedSettings.colorTheme];
  const biasColorForTheme = (bias: Bias): string => {
    if (bias === 'Left') return palette.left;
    if (bias === 'Right') return palette.right;
    if (bias === 'Even') return palette.even;
    return palette.na;
  };

  const sections = analysis.lateralization.sectionSummaries;
  const tests = visibleTestKeys
    ? filterLateralizationTests(analysis.lateralization.tests, visibleTestKeys)
    : analysis.lateralization.tests;
  const showCervical = visibleTestKeys ? isCervicalBatteryVisible(visibleTestKeys) : true;
  const diagramSections = SOP_SECTION_ORDER.filter(
    (sectionKey) => sectionKey !== 'cervical' || showCervical
  );

  const getTest = (testKey: string): TestLateralization | undefined => tests.find((t) => t.testKey === testKey);
  const hasHyperInSection = (section: string): boolean => tests.some((t) => t.section === section && t.isHypermobile);
  const hasInvalidInSection = (section: string): boolean => tests.some((t) => t.section === section && t.isInvalidated);

  const carTest = getTest('CervicalAxialRotation');
  const clfTest = getTest('CervicalLateralFlexion');
  const shoulderIr = getTest('ShoulderIR');
  const shoulderEr = getTest('ShoulderER');
  const femoralIr = getTest('FemoralIR');
  const femoralEr = getTest('FemoralER');

  // Use primary rotation-facing tests for big arrows so changes are responsive even when section summary is "Even".
  const thoraxFacingTest = getTest('ShoulderAbduction');
  const pelvisFacingTest = getTest('PelvicRotation');

  const torsoBias = thoraxFacingTest?.bias ?? null;
  const pelvisBias = pelvisFacingTest?.bias ?? null;

  const upperLeftIcon = resolveLimbIcon(shoulderIr?.leftVal ?? null, shoulderEr?.leftVal ?? null);
  const upperRightIcon = resolveLimbIcon(shoulderIr?.rightVal ?? null, shoulderEr?.rightVal ?? null);
  const lowerLeftIcon = resolveLimbIcon(femoralIr?.leftVal ?? null, femoralEr?.leftVal ?? null);
  const lowerRightIcon = resolveLimbIcon(femoralIr?.rightVal ?? null, femoralEr?.rightVal ?? null);

  return (
    <div className="grid h-full w-full grid-rows-[auto,1fr,auto] items-center gap-2 min-h-0" data-testid="body-diagram">
      <p className="text-center text-xs uppercase tracking-wide text-muted-foreground">Lateralization Map</p>
      <div className="min-h-0 w-full overflow-hidden">
        <div className="mx-auto flex h-full max-h-full w-full max-w-[540px] items-center justify-center">
          <div className="h-full max-h-full w-auto max-w-full" style={{ aspectRatio: '5 / 9' }}>
            <svg
              viewBox="0 0 300 540"
              className="h-full w-full text-foreground"
              role="img"
              aria-label="Interactive body lateralization diagram"
            >
        <image
          href={bodyTemplateUrl}
          x="0"
          y="0"
          width="300"
          height="540"
          preserveAspectRatio="xMidYMid meet"
          opacity={resolvedSettings.backgroundOpacity}
        />

        {diagramSections.map((sectionKey) => {
          const bias = (sections[sectionKey] as keyof typeof SOP_BIAS_THEME) ?? 'N/A';
          const tooltipText = sectionValues(tests, sectionKey);
          const isSelected = selectedRegion === sectionKey;
          const zone = SECTION_HIT_ZONES[sectionKey];

          return (
            <TooltipProvider key={sectionKey}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <rect
                    role="button"
                    aria-label={`${SOP_SECTION_LABELS[sectionKey]} — ${bias}`}
                    className={cn('cursor-pointer transition-all', isSelected && 'opacity-80')}
                    onClick={() => onRegionClick?.(sectionKey)}
                    x={zone.x}
                    y={zone.y}
                    width={zone.w}
                    height={zone.h}
                    fill="transparent"
                  />
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-xs text-xs whitespace-pre-wrap">
                  <p className="font-semibold">
                    {`${SOP_SECTION_LABELS[sectionKey]} — ${bias === 'N/A' ? 'No data' : bias}`}
                  </p>
                  <p className="mt-1 text-muted-foreground">{tooltipText}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        })}

        <PlacedIcon
          icon={resolveFacingIcon(torsoBias, thoraxFacingTest?.leftVal ?? null, thoraxFacingTest?.rightVal ?? null)}
          placement={{ ...PLACEMENTS.torso, size: PLACEMENTS.torso.size * arrowScaleMultiplier }}
          color={biasColorForTheme(torsoBias)}
          isHyper={hasHyperInSection('thorax')}
          isInvalid={hasInvalidInSection('thorax')}
          tooltipLines={buildDirectionalTooltip('Ribcage facing direction', thoraxFacingTest)}
        />

        <PlacedIcon
          icon={resolveFacingIcon(pelvisBias, pelvisFacingTest?.leftVal ?? null, pelvisFacingTest?.rightVal ?? null)}
          placement={{ ...PLACEMENTS.pelvis, size: PLACEMENTS.pelvis.size * arrowScaleMultiplier }}
          color={biasColorForTheme(pelvisBias)}
          isHyper={hasHyperInSection('pelvis')}
          isInvalid={hasInvalidInSection('pelvis')}
          tooltipLines={buildDirectionalTooltip('Pelvis facing direction', pelvisFacingTest)}
        />

        {showCervical && (
          <>
        <PlacedIcon
          icon={resolveFacingIcon(carTest?.bias ?? null, carTest?.leftVal ?? null, carTest?.rightVal ?? null)}
          placement={{ ...PLACEMENTS.car, size: PLACEMENTS.car.size * arrowScaleMultiplier }}
          color={biasColorForTheme(carTest?.bias ?? null)}
          isHyper={Boolean(carTest?.isHypermobile)}
          isInvalid={Boolean(carTest?.isInvalidated)}
          tooltipLines={buildDirectionalTooltip('Cervical axial rotation (CAR)', carTest)}
        />

        <PlacedIcon
          icon={resolveClfIcon(clfTest?.bias ?? null, clfTest?.leftVal ?? null, clfTest?.rightVal ?? null)}
          placement={{ ...PLACEMENTS.clf, size: PLACEMENTS.clf.size * arrowScaleMultiplier }}
          color={biasColorForTheme(clfTest?.bias ?? null)}
          isHyper={Boolean(clfTest?.isHypermobile)}
          isInvalid={Boolean(clfTest?.isInvalidated)}
          tooltipLines={buildDirectionalTooltip('Cervical lateral flexion (CLF)', clfTest)}
        />
          </>
        )}

        {resolvedSettings.showLimbArrows && (
          <>
            <PlacedIcon
              icon={upperLeftIcon}
              placement={{ ...PLACEMENTS.elbowLeft, size: PLACEMENTS.elbowLeft.size * arrowScaleMultiplier }}
              color={palette.left}
              isHyper={Boolean(shoulderIr?.isHypermobile || shoulderEr?.isHypermobile)}
              isInvalid={Boolean(shoulderIr?.isInvalidated && shoulderEr?.isInvalidated)}
              tooltipLines={buildLimbTooltip('Left upper limb IR/ER bias', 'Left', shoulderIr, shoulderEr)}
            />

            <PlacedIcon
              icon={upperRightIcon}
              placement={{ ...PLACEMENTS.elbowRight, size: PLACEMENTS.elbowRight.size * arrowScaleMultiplier }}
              color={palette.right}
              isHyper={Boolean(shoulderIr?.isHypermobile || shoulderEr?.isHypermobile)}
              isInvalid={Boolean(shoulderIr?.isInvalidated && shoulderEr?.isInvalidated)}
              tooltipLines={buildLimbTooltip('Right upper limb IR/ER bias', 'Right', shoulderIr, shoulderEr)}
            />

            <PlacedIcon
              icon={lowerLeftIcon}
              placement={{ ...PLACEMENTS.kneeLeft, size: PLACEMENTS.kneeLeft.size * arrowScaleMultiplier }}
              color={palette.left}
              isHyper={Boolean(femoralIr?.isHypermobile || femoralEr?.isHypermobile)}
              isInvalid={Boolean(femoralIr?.isInvalidated && femoralEr?.isInvalidated)}
              tooltipLines={buildLimbTooltip('Left lower limb IR/ER bias', 'Left', femoralIr, femoralEr)}
            />

            <PlacedIcon
              icon={lowerRightIcon}
              placement={{ ...PLACEMENTS.kneeRight, size: PLACEMENTS.kneeRight.size * arrowScaleMultiplier }}
              color={palette.right}
              isHyper={Boolean(femoralIr?.isHypermobile || femoralEr?.isHypermobile)}
              isInvalid={Boolean(femoralIr?.isInvalidated && femoralEr?.isInvalidated)}
              tooltipLines={buildLimbTooltip('Right lower limb IR/ER bias', 'Right', femoralIr, femoralEr)}
            />
          </>
        )}

        {resolvedSettings.showSectionLabels && diagramSections.map((sectionKey) => {
          const bias = (sections[sectionKey] as keyof typeof SOP_BIAS_THEME) ?? 'N/A';
          const sectionColor = biasColorForTheme(bias as Bias);
          const labelPositions: Record<string, { x: number; y: number }> = {
            cervical: { x: 196, y: 48 },
            thorax: { x: 240, y: 130 },
            pelvis: { x: 210, y: 265 },
            femoral: { x: 212, y: 350 },
          };
          const pos = labelPositions[sectionKey];
          return (
            <text
              key={`label-${sectionKey}`}
              x={pos.x}
              y={pos.y}
              fontSize={9}
              fill={sectionColor}
              fontWeight="600"
              className="select-none"
              textAnchor="start"
            >
              {SOP_SECTION_LABELS[sectionKey]}
            </text>
          );
        })}
            </svg>
          </div>
        </div>
      </div>

      {resolvedSettings.showLegend && (
        <div className="flex w-full max-w-full flex-wrap items-center justify-center gap-x-3 gap-y-1 px-1 text-[10px] text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <IoArrowRedoOutline size={12} color={palette.left} /> IR
          </span>
          <span className="inline-flex items-center gap-1">
            <IoArrowUndoOutline size={12} color={palette.right} /> ER
          </span>
          <span className="inline-flex items-center gap-1">
            <IoArrowForward size={12} color={palette.left} /> Facing L
          </span>
          <span className="inline-flex items-center gap-1">
            <IoArrowBack size={12} color={palette.right} /> Facing R
          </span>
          <span className="inline-flex items-center gap-1">
            <VscArrowBoth size={12} color={palette.even} /> Balanced
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: SOP_ACCENT_THEME.hypermobileHex, boxShadow: `0 0 4px ${SOP_ACCENT_THEME.hypermobileHex}` }} />
            Hyper
          </span>
        </div>
      )}
    </div>
  );
}
